/**
 * Utility for formatting call transcripts for export
 * Handles both structured and plain text transcripts with speaker attribution and timestamps
 */

/**
 * Parse structured transcript JSON
 * @param {string} transcriptData - JSON string containing structured transcript
 * @returns {Array} Array of transcript segments with speaker, text, and timestamp
 */
const parseStructuredTranscript = (transcriptData) => {
  try {
    const parsed = JSON.parse(transcriptData)

    if (Array.isArray(parsed)) {
      return parsed
    }

    if (parsed.transcript) {
      if (Array.isArray(parsed.transcript)) {
        return parsed.transcript
      }
      if (typeof parsed.transcript === 'string') {
        return parseTextTranscript(parsed.transcript)
      }
    }

    if (parsed.segments) {
      return parsed.segments
    }

    return []
  } catch (e) {
    return parseTextTranscript(transcriptData)
  }
}

/**
 * Parse plain text transcript and identify speakers
 * @param {string} text - Plain text transcript
 * @returns {Array} Array of transcript segments with speaker attribution
 */
const parseTextTranscript = (text) => {
  if (!text || typeof text !== 'string') return []

  const segments = []
  const lines = text.split('\n').filter(line => line.trim())

  let currentSpeaker = null
  let currentContent = []
  let segmentStartTime = 0
  const avgWordsPerSecond = 2.5

  lines.forEach((line) => {
    const trimmedLine = line.trim()

    if (trimmedLine.startsWith('Agent:') || trimmedLine.startsWith('Customer:') || trimmedLine.startsWith('Caller:')) {
      if (currentSpeaker && currentContent.length > 0) {
        const content = currentContent.join(' ')
        const wordCount = content.split(/\s+/).length
        const duration = Math.round(wordCount / avgWordsPerSecond)

        segments.push({
          speaker: currentSpeaker,
          text: content,
          timestamp: segmentStartTime
        })

        segmentStartTime += duration
      }

      if (trimmedLine.startsWith('Agent:')) {
        currentSpeaker = 'Agent'
        currentContent = [trimmedLine.replace(/^Agent:\s*/, '')]
      } else if (trimmedLine.startsWith('Customer:') || trimmedLine.startsWith('Caller:')) {
        currentSpeaker = 'Customer'
        currentContent = [trimmedLine.replace(/^(Customer|Caller):\s*/, '')]
      }
    } else if (currentContent.length > 0) {
      currentContent.push(trimmedLine)
    } else {
      currentSpeaker = 'Agent'
      currentContent = [trimmedLine]
    }
  })

  if (currentSpeaker && currentContent.length > 0) {
    const content = currentContent.join(' ')
    segments.push({
      speaker: currentSpeaker,
      text: content,
      timestamp: segmentStartTime
    })
  }

  return segments
}

/**
 * Format timestamp in MM:SS format
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted timestamp
 */
const formatTimestamp = (seconds) => {
  const minutes = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

/**
 * Extract call information from selectedRow
 * @param {Object|Array} selectedRow - Call data from the table
 * @returns {Object} Extracted call information
 */
const extractCallInfo = (selectedRow) => {
  if (!selectedRow) {
    return {
      agentName: 'Unknown Agent',
      customerName: 'Unknown Customer',
      campaignName: 'Unknown Campaign',
      callDate: new Date().toISOString(),
      callId: 'Unknown',
      direction: 'Unknown',
      disposition: 'Unknown'
    }
  }

  if (selectedRow.call_id !== undefined) {
    return {
      agentName: selectedRow.agent_name || 'Unknown Agent',
      customerName: selectedRow.caller_name || selectedRow.customer_name || 'Unknown Customer',
      campaignName: selectedRow.campaign_name || 'Unknown Campaign',
      callDate: selectedRow.call_date || new Date().toISOString(),
      callId: selectedRow.call_id || 'Unknown',
      direction: selectedRow.direction || 'Unknown',
      disposition: selectedRow.disposition || 'Unknown'
    }
  }

  if (selectedRow.id && selectedRow[0] !== undefined) {
    return {
      agentName: selectedRow[6] || 'Unknown Agent',
      customerName: selectedRow[17] || 'Unknown Customer',
      campaignName: selectedRow[5] || selectedRow[18] || 'Unknown Campaign',
      callDate: selectedRow[0] || new Date().toISOString(),
      callId: selectedRow.id || 'Unknown',
      direction: selectedRow[4] || 'Unknown',
      disposition: selectedRow[13] || 'Unknown'
    }
  }

  if (Array.isArray(selectedRow)) {
    return {
      agentName: selectedRow[6] || 'Unknown Agent',
      customerName: selectedRow[17] || 'Unknown Customer',
      campaignName: selectedRow[5] || selectedRow[18] || 'Unknown Campaign',
      callDate: selectedRow[0] || new Date().toISOString(),
      callId: selectedRow[3] || selectedRow.id || 'Unknown',
      direction: selectedRow[4] || 'Unknown',
      disposition: selectedRow[13] || 'Unknown'
    }
  }

  return {
    agentName: selectedRow.agentName || selectedRow.agent || 'Unknown Agent',
    customerName: selectedRow.customerName || selectedRow.customer || 'Unknown Customer',
    campaignName: selectedRow.campaignName || selectedRow.campaign || 'Unknown Campaign',
    callDate: selectedRow.callDate || selectedRow.date || new Date().toISOString(),
    callId: selectedRow.id || selectedRow.callId || 'Unknown',
    direction: selectedRow.direction || selectedRow.callType || 'Unknown',
    disposition: selectedRow.disposition || selectedRow.outcome || 'Unknown'
  }
}

/**
 * Format transcript for export
 * @param {string} transcriptData - Raw transcript data (JSON or plain text)
 * @param {Object|Array} selectedRow - Call data from the table
 * @returns {string} Formatted transcript text
 */
export const formatTranscriptForExport = (transcriptData, selectedRow) => {
  if (!transcriptData) {
    return 'No transcript available for this call.'
  }

  const callInfo = extractCallInfo(selectedRow)

  let segments = []

  if (transcriptData.trim().startsWith('{') || transcriptData.trim().startsWith('[')) {
    segments = parseStructuredTranscript(transcriptData)
  } else {
    segments = parseTextTranscript(transcriptData)
  }

  const header = `${'='.repeat(80)}
CALL TRANSCRIPT
Call ID: ${callInfo.callId}
Date: ${callInfo.callDate}
Direction: ${callInfo.direction}
Disposition: ${callInfo.disposition}
Agent: ${callInfo.agentName}
Customer: ${callInfo.customerName}
Campaign: ${callInfo.campaignName}
Generated: ${new Date().toLocaleString()}
${'='.repeat(80)}

`

  const formattedSegments = segments.map(segment => {
    const timestamp = formatTimestamp(segment.timestamp || 0)
    const speaker = segment.speaker || 'Unknown'
    const text = segment.text || segment.content || ''

    return `[${timestamp}] ${speaker}: ${text}`
  }).join('\n\n')

  return header + (formattedSegments || transcriptData)
}

/**
 * Check if transcript has valid structure
 * @param {string} transcriptData - Raw transcript data
 * @returns {boolean} True if transcript is structured
 */
export const isStructuredTranscript = (transcriptData) => {
  if (!transcriptData || typeof transcriptData !== 'string') return false

  try {
    const trimmed = transcriptData.trim()
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      JSON.parse(trimmed)
      return true
    }
  } catch (e) {
  }

  return transcriptData.includes('Agent:') || transcriptData.includes('Customer:') || transcriptData.includes('Caller:')
}